import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import SeoHead from "@/components/feature/SeoHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { kuaibaoList } from "@/mocks/kuaibao";
import type { KuaibaoItem } from "@/types/kuaibao";
import { formatDateDay, formatDateLabel, formatHeat, readingTime } from "@/utils/format";

const allItems = kuaibaoList as KuaibaoItem[];

export default function KuaibaoDetail() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  const item = allItems.find((entry) => entry.slug === slug);

  if (!item) {
    return (
      <>
        <SiteNavbar />
        <main className="w-full pt-16 md:pt-20">
          <div className="w-full px-4 md:px-8 py-24 text-center">
            <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
              <i className="ri-file-unknow-line text-3xl leading-none"></i>
            </span>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-3">
              这条快报找不到了
            </h1>
            <p className="font-label text-sm text-foreground-500 mb-6">
              它可能已被合并或更新，去快报列表看看今天还有什么热点吧。
            </p>
            <Link
              to="/kuaibao"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line leading-none"></i>
              返回快报列表
            </Link>
          </div>
        </main>
        <SiteFooter />
      </>
    );
  }

  const related = allItems
    .filter((entry) => entry.slug !== item.slug && entry.categorySlug === item.categorySlug)
    .slice(0, 3);
  const fallback = allItems
    .filter((entry) => entry.slug !== item.slug && entry.categorySlug !== item.categorySlug)
    .sort((a, b) => b.heat - a.heat)
    .slice(0, Math.max(0, 3 - related.length));
  const relatedList = [...related, ...fallback];

  const hottest = [...allItems].sort((a, b) => b.heat - a.heat).slice(0, 5);
  const minutes = readingTime(item.content);

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: item.title,
    description: item.summary,
    image: item.cover,
    datePublished: item.publishedAt.replace(" ", "T") + ":00+08:00",
    dateModified: item.publishedAt.replace(" ", "T") + ":00+08:00",
    inLanguage: "zh-CN",
    articleSection: item.category,
    keywords: item.tags.join(","),
    author: { "@type": "Organization", name: "51.cg 今日吃瓜单" },
    publisher: {
      "@type": "Organization",
      name: "51.cg 今日吃瓜单",
      url: "https://hizmfjw.cn/",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://hizmfjw.cn/kuaibao/${item.slug}`,
    },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://hizmfjw.cn/" },
      { "@type": "ListItem", position: 2, name: "吃瓜快报", item: "https://hizmfjw.cn/kuaibao" },
      {
        "@type": "ListItem",
        position: 3,
        name: item.title,
        item: `https://hizmfjw.cn/kuaibao/${item.slug}`,
      },
    ],
  };

  return (
    <>
      <SeoHead
        title={`${item.title} - 51.cg今日吃瓜单`}
        description={item.summary}
        keywords={`${item.category},${item.tags.join(",")},51.cg今日吃瓜单,吃瓜快报`}
        path={`/kuaibao/${item.slug}`}
        jsonLd={[articleLd, breadcrumbLd]}
      />
      <SiteNavbar />
      <main className="w-full pt-16 md:pt-20">
        <section className="w-full bg-background-100 border-b border-background-200/70 pt-7 md:pt-10 pb-8 md:pb-10">
          <div className="w-full px-4 md:px-8">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "吃瓜快报", to: "/kuaibao" },
                { label: item.title },
              ]}
            />
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <Link
                to={`/kuaibao?cat=${item.categorySlug}`}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-secondary-100 text-secondary-800 font-label text-xs"
              >
                <i className="ri-price-tag-3-line leading-none"></i>
                {item.category}
              </Link>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-primary-100 text-primary-800 font-label text-xs">
                <i className="ri-fire-line leading-none"></i>
                热度 {formatHeat(item.heat)}
              </span>
            </div>
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950 mt-4 mb-4 leading-snug max-w-4xl">
              {item.title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 font-label text-xs md:text-sm text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line leading-none"></i>
                {item.publishedAt}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-book-read-line leading-none"></i>
                约 {minutes}读完
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-live-line leading-none"></i>
                快报速览
              </span>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-8 md:py-12">
          <div className="w-full px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
              <article className="lg:col-span-8">
                <div className="w-full h-56 md:h-80 rounded-lg overflow-hidden mb-7">
                  <img
                    src={item.cover}
                    alt={`${item.title} 相关配图`}
                    title={`${item.title} - 51.cg今日吃瓜单`}
                    className="w-full h-56 md:h-80 object-top object-cover"
                  />
                </div>

                <p className="font-label text-base md:text-lg text-foreground-700 leading-relaxed mb-6 pl-4 border-l-2 border-primary-400">
                  {item.summary}
                </p>

                <div className="flex flex-col gap-5">
                  {item.content.map((para, i) => (
                    <p
                      key={`${item.slug}-p-${i}`}
                      className="font-label text-[15px] md:text-base text-foreground-700 leading-8"
                    >
                      {para}
                    </p>
                  ))}
                </div>

                <div className="mt-8 pt-6 border-t border-background-200">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-label text-xs text-foreground-400 mr-1">相关标签</span>
                    {item.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/kuaibao?q=${encodeURIComponent(tag)}`}
                        className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-900 font-label text-xs transition-colors"
                      >
                        #{tag}
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="mt-8 rounded-lg bg-accent-100 border border-accent-200 p-5 md:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <h2 className="font-heading font-semibold text-accent-950 text-base md:text-lg mb-1">
                      想第一时间收到这类快报？
                    </h2>
                    <p className="font-label text-sm text-accent-900/80">
                      订阅每日瓜单邮件，只挑十条最值得看的热点。
                    </p>
                  </div>
                  <Link
                    to="/#subscribe"
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium transition-colors whitespace-nowrap shrink-0"
                  >
                    <i className="ri-notification-3-line leading-none"></i>
                    订阅快报
                  </Link>
                </div>
              </article>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-24 flex flex-col gap-6">
                  <div className="bg-background-100 border border-background-200/80 rounded-lg p-5">
                    <h2 className="font-heading font-semibold text-foreground-950 text-base mb-4 flex items-center gap-2">
                      <i className="ri-fire-line text-primary-500 leading-none"></i>
                      今日最热
                    </h2>
                    <ol className="flex flex-col gap-1">
                      {hottest.map((hot, i) => (
                        <li key={hot.slug}>
                          <Link
                            to={`/kuaibao/${hot.slug}`}
                            className="flex items-start gap-3 p-2 rounded-md hover:bg-background-50 transition-colors group"
                          >
                            <span
                              className={`w-6 h-6 shrink-0 flex items-center justify-center rounded font-heading text-xs font-bold ${
                                i < 3
                                  ? "bg-primary-500 text-background-50"
                                  : "bg-background-200 text-foreground-600"
                              }`}
                            >
                              {i + 1}
                            </span>
                            <span className="font-label text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                              {hot.title}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ol>
                  </div>

                  <div className="bg-background-100 border border-background-200/80 rounded-lg p-5">
                    <h2 className="font-heading font-semibold text-foreground-950 text-base mb-4 flex items-center gap-2">
                      <i className="ri-links-line text-secondary-600 leading-none"></i>
                      同类快报
                    </h2>
                    <ul className="flex flex-col gap-4">
                      {relatedList.map((rel) => (
                        <li key={rel.slug}>
                          <Link to={`/kuaibao/${rel.slug}`} className="flex gap-3 group">
                            <span className="w-20 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                              <img
                                src={rel.cover}
                                alt={`${rel.title} 配图`}
                                title={`${rel.title} - 51.cg今日吃瓜单`}
                                className="w-20 h-16 object-top object-cover"
                              />
                            </span>
                            <span className="flex flex-col min-w-0">
                              <span className="font-heading font-semibold text-sm text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                                {rel.title}
                              </span>
                              <span className="font-label text-xs text-foreground-400 mt-1">
                                {formatDateDay(rel.publishedAt)} {formatDateLabel(rel.publishedAt)}
                              </span>
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/kuaibao"
                      className="mt-5 inline-flex items-center gap-1.5 font-label text-sm text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      查看全部快报
                      <i className="ri-arrow-right-line leading-none"></i>
                    </Link>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}